import { defaultPaginationQuery, QueryOptions } from 'sync/query';
import { deleteRequest, getRequest, postRequest, putRequest } from 'sync/request';
import { OrganizationContext, OrganizationUserContext } from 'sync/request.types';
import {
  ActivationAndRestrictionResponse,
  CreateOrganizationUserPayload,
  CreateUserResponse,
  Organization,
  OrganizationGetResponse,
  OrganizationListResponse,
  OrganizationUserListResponse,
  OrganizationUserResponse,
  UpdateOrganizationPayload,
  UpdateOrganizationUserPermissionsPayload,
  UserLoginTypeResponse,
} from './organization-service.types';

/**
 * Return a list of organizations.
 */
export function listOrganizations(params: QueryOptions = defaultPaginationQuery) {
  return getRequest<OrganizationListResponse>('/v2/organizations', { params });
}

/**
 * Return a specified organization
 */
export function getOrganization({ organizationId }: OrganizationContext) {
  return getRequest<OrganizationGetResponse>(`/v2/organizations/${organizationId}`);
}

/**
 * Update a specified organization
 */
export function updateOrganization({ organizationId }: OrganizationContext, data: UpdateOrganizationPayload) {
  return putRequest<Organization>(`/v2/organizations/${organizationId}`, { data });
}

/**
 * Return a list of users associated with a given organization.
 */
export function listOrganizationUsers({ organizationId }: OrganizationContext, params: QueryOptions = defaultPaginationQuery) {
  return getRequest<OrganizationUserListResponse>(`/v2/organizations/${organizationId}/users`, { params });
}

/**
 * Return a specified user associated with a given organization.
 */
export function getOrganizationUser({ organizationId, userId }: OrganizationUserContext) {
  return getRequest<OrganizationUserResponse>(`/v2/organizations/${organizationId}/users/${userId}`);
}

/**
 * Create a user against a specified organization.
 */
export function createOrganizationUser({ organizationId }: OrganizationContext, data: CreateOrganizationUserPayload) {
  return postRequest<CreateUserResponse>(`/v2/organizations/${organizationId}/users`, { data });
}

/**
 * Update organization permissions for a specified user.
 */
export function updateOrganizationUserPermissions({ organizationId }: OrganizationContext, data: UpdateOrganizationUserPermissionsPayload) {
  return putRequest<{}>(`/v2/organizations/${organizationId}/permissions`, { data });
}

/**
 * Resends email with invite link for a specified organization user.
 */
export function resendOrganizationUser({ organizationId, userId }: OrganizationUserContext) {
  return postRequest<{}>(`/v2/organizations/${organizationId}/users/${userId}/resend`);
}

/**
 * Delete a specified organization user.
 */
export function deleteOrganizationUser({ organizationId, userId }: OrganizationUserContext) {
  return deleteRequest<{}>(`/v2/organizations/${organizationId}/users/${userId}`);
}

/**
 * Check user login type
 */
export function checkLoginType({ email }: { email: string }) {
  return getRequest<UserLoginTypeResponse>(`/v2/auth/login-type?email=${email}`);
}

/**
 * Check activation of the current org and restrictions whether they can create cluster/columnar
 */

export function getActivationAndRestriction({ organizationId }: OrganizationContext) {
  return getRequest<ActivationAndRestrictionResponse>(`/v2/organizations/${organizationId}/myactivation`);
}
